import Seo from "@/components/Seo";
import PageLayout from "@/components/PageLayout";
import PageHero from "@/components/sections/PageHero";

/**
 * Reusable wrapper for the body content of legal pages.
 * Consistent typography, narrow column, plenty of breathing room.
 */
export function LegalBody({ updated, children }) {
  return (
    <section
      data-testid="legal-body"
      className="relative bg-[var(--crest-cream)] text-[#1a1a1a] py-20 md:py-28"
    >
      <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12">
        <div
          data-testid="legal-last-updated"
          className="reveal font-display uppercase tracking-[0.35em] text-[10px] font-semibold text-[var(--crest-amber)] mb-12 pb-4 border-b border-[#1a1a1a]/15"
        >
          Last updated — {updated}
        </div>
        <div className="legal-prose space-y-12">{children}</div>
      </div>
      <style>{`
        .legal-prose h2 {
          font-family: "Barlow Condensed", sans-serif;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: -0.01em;
          font-size: clamp(1.5rem, 2.6vw, 2rem);
          line-height: 1;
          margin-bottom: 1.25rem;
          color: #1a1a1a;
        }
        .legal-prose h3 {
          font-family: "Barlow Condensed", sans-serif;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-size: 0.95rem;
          margin-top: 1.5rem;
          margin-bottom: 0.6rem;
          color: #1a1a1a;
        }
        .legal-prose p {
          font-family: "IBM Plex Sans", sans-serif;
          font-size: 16px;
          line-height: 1.7;
          color: #2f2f2f;
        }
        .legal-prose p + p { margin-top: 1rem; }
        .legal-prose ul {
          font-family: "IBM Plex Sans", sans-serif;
          font-size: 16px;
          line-height: 1.7;
          color: #2f2f2f;
          padding-left: 0;
          list-style: none;
          margin-top: 0.75rem;
        }
        .legal-prose li {
          position: relative;
          padding-left: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .legal-prose li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.7rem;
          width: 0.5rem;
          height: 1px;
          background: var(--crest-amber);
        }
        .legal-prose a {
          color: #b45309;
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .legal-prose a:hover { color: var(--crest-amber); }
        .legal-prose strong { color: #1a1a1a; font-weight: 600; }
        .legal-section {
          padding-bottom: 2rem;
          border-bottom: 1px solid rgba(26, 26, 26, 0.1);
        }
        .legal-section:last-child { border-bottom: none; padding-bottom: 0; }
      `}</style>
    </section>
  );
}

export function LegalSection({ heading, id, children }) {
  return (
    <div className="legal-section reveal" id={id}>
      <h2>{heading}</h2>
      {children}
    </div>
  );
}

// Re-exports for convenience — pages can import everything they need from here.
export { Seo, PageLayout, PageHero };
